import React, { useContext, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { UserContext } from "../contexts/UserContext";
import Sidebar from "../common/Sidebar";
import Header from "./common/Header";
import ShieldProgressBar from "./common/ShieldProgressBar";
import MobileTopbar from "../common/MobileTopbar";
import MobileSidebarDrawer from "../common/MobileSidebarDrawer";
import { SyllabusContext } from "../contexts/SyllabusContext";
import { activateMasteryFlow } from "../utils/masteryFlow";
import { FiX, FiSearch } from "react-icons/fi";
import { FaGem } from "react-icons/fa";
import SimpleThreeDotsLoader from "./SimpleThreeDotsLoader";
import {
  getStoredLearningSubject,
  persistLearningSubjectSelection,
} from "../utils/learningSubjectStorage";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;

const DEFAULT_SUBJECTS = [];
const DEFAULT_BOARD_VALUE = "CBSE";

const SHIELD_TIERS = [
  { id: "bronze", label: "Bronze Shield", percent: 34 },
  { id: "silver", label: "Silver Shield", percent: 55 },
  { id: "gold", label: "Gold Shield", percent: 74 },
  { id: "diamond", label: "Diamond Shield", percent: 90 },
];

const clampPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Math.min(100, Math.max(0, numeric));
};

const parseShieldPercentage = (value) => {
  if (value === null || typeof value === "undefined") {
    return null;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return numeric;
};

const parseFiniteNumber = (value) => {
  if (value === null || typeof value === "undefined") {
    return null;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return numeric;
};

const getCurrentShieldTier = (percent) => {
  const normalized = clampPercent(percent);
  return SHIELD_TIERS.reduce((currentTier, tier) => {
    if (normalized >= tier.percent) {
      return tier;
    }
    return currentTier;
  }, null);
};

const getNextShieldTier = (percent) => {
  const normalized = clampPercent(percent);
  return SHIELD_TIERS.find((tier) => normalized < tier.percent) || null;
};

const ordinalSuffix = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return "th";
  }
  const remainder100 = number % 100;
  if (remainder100 >= 11 && remainder100 <= 13) {
    return "th";
  }
  switch (number % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const getGradeKeyForLearning = (grade) => {
  if (grade === null || grade === undefined) {
    return "10th Grade";
  }

  const gradeStr = String(grade).trim();
  if (!gradeStr.length) {
    return "10th Grade";
  }

  if (/grade/i.test(gradeStr)) {
    return gradeStr;
  }

  if (!/^\d+$/.test(gradeStr)) {
    return gradeStr;
  }

  return `${gradeStr}${ordinalSuffix(gradeStr)} Grade`;
};

const NewLearnComponent = () => {
  const { userData, updateProfileDetails, fetchUserProfile } =
    useContext(UserContext);
  const syllabusContext = useContext(SyllabusContext);
  const {
    subjects: syllabusSubjects,
    syllabusData,
    subjectChapters,
    isLoading: syllabusLoading,
    fetchSyllabus,
  } = syllabusContext || {};
  const subjects =
    Array.isArray(syllabusSubjects) && syllabusSubjects.length > 0
      ? syllabusSubjects
      : DEFAULT_SUBJECTS;
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  console.log("subjects:", subjects);
  const [selectedSubject, setSelectedSubject] = useState(() => {
    const fallbackSubject = subjects[0] || DEFAULT_SUBJECTS[0] || null;

    const storedSubject = getStoredLearningSubject();
    if (storedSubject) {
      return storedSubject;
    }

    if (fallbackSubject) {
      persistLearningSubjectSelection(fallbackSubject);
    }

    return fallbackSubject;
  });
  const [currentTopic, setCurrentTopic] = useState("Quadratic Equations");
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [isSettingLevel, setIsSettingLevel] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [shouldBypassLearningLevel, setShouldBypassLearningLevel] =
    useState(false);

  // Fetch user profile on mount
  useEffect(() => {
    let isMounted = true;

    const loadUserProfile = async () => {
      try {
        if (typeof fetchUserProfile === "function") {
          await fetchUserProfile();
        }
      } finally {
        // Cleanup handled by isMounted flag
      }
    };

    loadUserProfile();

    return () => {
      isMounted = false;
    };
    // We intentionally fetch the profile once when the component loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof fetchSyllabus === "function") {
      fetchSyllabus();
    }
    // Only trigger once on mount to guarantee the syllabus list starts loading with the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedSubject) {
      return;
    }
    persistLearningSubjectSelection(selectedSubject);
  }, [selectedSubject]);

  const proficiencyLevels = [
    {
      id: "beginner",
      title: "BEGINNER",
      subtitle: "",
      description: "I need to learn this from the start.",
      height: "h-32", // Shortest
      value: 0,
    },
    {
      id: "intermediate",
      title: "INTERMEDIATE",
      subtitle: "",
      description: "I know the theory, but I struggle with problems.",
      height: "h-40", // Taller
      value: 20,
    },
    {
      id: "proficient",
      title: "PROFICIENT",
      subtitle: "",
      description: "I usually get it right, but I need to ace my tests.",
      height: "h-44", // Taller
      value: 40,
    },
    {
      id: "advanced",
      title: "ADVANCED",
      subtitle: "",
      description: "I am ready for hard problems.",
      height: "h-48", // Tallest
      value: 60,
    },
  ];

  const userGrade = userData?.grade;
  const gradeKey = getGradeKeyForLearning(userGrade);

  const learningCurrentLevel = userData?.learning_current_level || {};
  const gradeLevelData =
    gradeKey && typeof learningCurrentLevel === "object"
      ? learningCurrentLevel[gradeKey]
      : undefined;

  const getSubjectEntryFromLearningLevel = (subject) => {
    if (!gradeLevelData || !subject || typeof subject !== "string") {
      return { entry: null, hasEntry: false };
    }

    const trimmedSubject = subject.trim();
    if (!trimmedSubject.length) {
      return { entry: null, hasEntry: false };
    }

    if (Object.prototype.hasOwnProperty.call(gradeLevelData, trimmedSubject)) {
      return {
        entry: gradeLevelData[trimmedSubject],
        hasEntry: true,
      };
    }

    const normalizedSubject = trimmedSubject.toLowerCase();
    const matchedKey = Object.keys(gradeLevelData).find((key) => {
      if (typeof key !== "string") {
        return false;
      }
      return key.trim().toLowerCase() === normalizedSubject;
    });

    if (matchedKey) {
      return {
        entry: gradeLevelData[matchedKey],
        hasEntry: true,
      };
    }

    const singleSubjectMatch =
      typeof gradeLevelData.subject === "string" &&
      gradeLevelData.subject.trim().toLowerCase() === normalizedSubject;

    if (singleSubjectMatch) {
      return {
        entry: gradeLevelData,
        hasEntry: true,
      };
    }

    return { entry: null, hasEntry: false };
  };

  const getChapterNameFromLevelEntry = (entry) => {
    if (!entry) {
      return null;
    }

    if (typeof entry === "string") {
      return entry;
    }

    const chapterValue = entry?.chapter ?? entry?.subject?.chapter ?? entry;

    if (typeof chapterValue === "string") {
      return chapterValue;
    }

    if (typeof chapterValue === "object" && chapterValue !== null) {
      return chapterValue?.name || chapterValue?.title || null;
    }

    return null;
  };

  const getProgressPercentFromLevel = (levelValue) => {
    const numericLevel = Number(levelValue);
    if (!Number.isFinite(numericLevel)) {
      return null;
    }
    const clampedLevel = Math.min(Math.max(numericLevel, 0), 4);
    return (clampedLevel / 4) * 100;
  };

  // Check if selected subject has data in learning_current_level
  const { entry: selectedSubjectData, hasEntry: selectedSubjectExists } =
    getSubjectEntryFromLearningLevel(selectedSubject);
  console.log("selectedSubjectData:", selectedSubjectData);
  const hasSubjectData =
    selectedSubjectExists &&
    selectedSubjectData !== null &&
    typeof selectedSubjectData !== "undefined";

  // Get chapter from learning_current_level if subject has data
  const currentLevelChapter = hasSubjectData
    ? getChapterNameFromLevelEntry(selectedSubjectData)
    : null;

  // Get shield percentage from learning_current_level if subject has data
  const rawShieldPercentage = hasSubjectData
    ? selectedSubjectData?.shield_percentage ??
      selectedSubjectData?.shieldPercentage ??
      null
    : null;

  const parsedShieldPercentage = parseShieldPercentage(rawShieldPercentage);

  const levelBasedProgress = hasSubjectData
    ? getProgressPercentFromLevel(selectedSubjectData?.level)
    : null;

  const shieldPercentageSource =
    parsedShieldPercentage !== null
      ? parsedShieldPercentage
      : levelBasedProgress;

  const shieldPercentage = clampPercent(
    shieldPercentageSource !== null ? shieldPercentageSource : 0
  );

  const currentShieldTier = getCurrentShieldTier(shieldPercentage);
  const nextShieldTier = getNextShieldTier(shieldPercentage);
  const remainingToNextShield = nextShieldTier
    ? Math.max(0, Math.ceil(nextShieldTier.percent - shieldPercentage))
    : null;
  const showSubjectDataLoader = syllabusLoading && hasSubjectData;
  const shouldShowLearningView = hasSubjectData;

  const normalizeValue = (value) =>
    typeof value === "string" ? value.replace(/\+/g, " ").trim() : null;

  // Helper: get first chapter name for a given subject from syllabus data
  const getFirstChapterForSubject = (subject) => {
    if (!subject || !Array.isArray(syllabusData)) {
      return null;
    }

    const normalizedSubject = subject.trim().toLowerCase();

    const matchingEntries = syllabusData.filter((entry) => {
      const entrySubject = (entry?.subject || "").trim().toLowerCase();
      if (!entrySubject || entrySubject !== normalizedSubject) {
        return false;
      }

      if (userGrade) {
        const entryGrade =
          entry?.grade ?? entry?.standard ?? entry?.class ?? entry?.gradeLevel;
        if (entryGrade && String(entryGrade) !== String(userGrade)) {
          return false;
        }
      }

      return true;
    });

    for (const entry of matchingEntries) {
      const chapters = Array.isArray(entry?.chapters) ? entry.chapters : [];
      if (chapters.length > 0) {
        const firstChapterName =
          chapters[0]?.name || chapters[0]?.title || null;
        if (firstChapterName && firstChapterName.trim().length > 0) {
          return firstChapterName;
        }
      }
    }

    return null;
  };

  const doesSubjectContainChapter = (subject, chapterName) => {
    if (
      !subject ||
      !chapterName ||
      !Array.isArray(syllabusData) ||
      syllabusData.length === 0
    ) {
      return false;
    }

    const normalizedSubject = subject.trim().toLowerCase();
    const normalizedChapter = chapterName.trim().toLowerCase();

    const matchingEntries = syllabusData.filter((entry) => {
      const entrySubject = (entry?.subject || "").trim().toLowerCase();
      if (!entrySubject || entrySubject !== normalizedSubject) {
        return false;
      }

      if (userGrade) {
        const entryGrade =
          entry?.grade ?? entry?.standard ?? entry?.class ?? entry?.gradeLevel;
        if (entryGrade && String(entryGrade) !== String(userGrade)) {
          return false;
        }
      }

      return true;
    });

    for (const entry of matchingEntries) {
      const chapters = Array.isArray(entry?.chapters) ? entry.chapters : [];
      const hasChapter = chapters.some((chapter) => {
        const chapterNameValue = (chapter?.name || chapter?.title || "")
          .trim()
          .toLowerCase();
        return chapterNameValue === normalizedChapter;
      });

      if (hasChapter) {
        return true;
      }
    }

    return false;
  };

  const searchParams = new URLSearchParams(location.search || "");

  const stateSubject = normalizeValue(location.state?.subject);
  const rawStateChapter =
    typeof location.state?.chapter === "string"
      ? location.state?.chapter
      : location.state?.chapter?.title || location.state?.chapter?.name;
  const stateChapter = normalizeValue(rawStateChapter);
  const querySubject = normalizeValue(searchParams.get("subject"));
  const queryChapter = normalizeValue(searchParams.get("chapter"));
  const preferredSubject = stateSubject || querySubject || null;
  const preferredChapter = stateChapter || queryChapter || null;

  // Track whether we've already applied the preferred subject/chapter from navigation
  const hasAppliedPreferredRef = useRef(false);

  // Get the latest chapter - use the one from learning_current_level if available, otherwise use currentTopic
  // This ensures all handlers and UI use the same source of truth from UserContext
  let latestChapter = currentTopic;
  if (!shouldBypassLearningLevel && hasSubjectData && currentLevelChapter) {
    latestChapter = currentLevelChapter;
  }

  // Topic data (title driven by latestChapter; shield progress mirrors learning_current_level)
  const topicData = {
    title: latestChapter || "Quadratic Equations",
    progress: shieldPercentage,
    mastery: shieldPercentage,
    nextGoal: nextShieldTier ? nextShieldTier.label : "Diamond Shield",
    currentShield: currentShieldTier,
    nextShield: nextShieldTier,
    remainingToNextShield,
  };

  let selectedChapterDetails = null;
  if (Array.isArray(syllabusData) && selectedSubject && currentTopic) {
    const normalizedSubject = selectedSubject.trim().toLowerCase();
    const normalizedChapter = currentTopic.trim().toLowerCase();

    const matchingEntries = syllabusData.filter((entry) => {
      const entrySubject = (entry?.subject || "").trim().toLowerCase();
      if (!entrySubject || entrySubject !== normalizedSubject) {
        return false;
      }

      if (userGrade) {
        const entryGrade =
          entry?.grade ?? entry?.standard ?? entry?.class ?? entry?.gradeLevel;
        if (entryGrade && String(entryGrade) !== String(userGrade)) {
          return false;
        }
      }

      return true;
    });

    for (const entry of matchingEntries) {
      const chapters = Array.isArray(entry?.chapters) ? entry.chapters : [];
      const match = chapters.find((chapter) => {
        const chapterName = (chapter?.name || chapter?.title || "")
          .trim()
          .toLowerCase();
        return chapterName === normalizedChapter;
      });

      if (match) {
        selectedChapterDetails = match;
        break;
      }
    }
  }

  useEffect(() => {
    if (!shouldBypassLearningLevel) {
      return;
    }

    const normalizedLevelChapter =
      typeof currentLevelChapter === "string"
        ? currentLevelChapter.trim().toLowerCase()
        : "";
    const normalizedCurrentTopic =
      typeof currentTopic === "string" ? currentTopic.trim().toLowerCase() : "";

    if (
      normalizedLevelChapter &&
      normalizedLevelChapter === normalizedCurrentTopic
    ) {
      setShouldBypassLearningLevel(false);
    }
  }, [shouldBypassLearningLevel, currentLevelChapter, currentTopic]);

  const handleLearnConcepts = () => {
    // Use the latestChapter derived from learning_current_level
    if (!selectedSubject || !latestChapter) {
      navigate("/practice/teachMeTopic");
      return;
    }

    const params = new URLSearchParams({
      enableWorkbookmode: "true",
      board: DEFAULT_BOARD_VALUE,
      subject: selectedSubject,
      chapter: latestChapter,
      from: "teachMe",
    });

    if (selectedChapterDetails?.docId) {
      params.set("docId", selectedChapterDetails.docId);
    }

    const isPracticeFlow = location.pathname.startsWith("/practice");
    const targetBasePath = isPracticeFlow ? "/practice/tutor" : "/tutor";
    navigate(`${targetBasePath}?${params.toString()}`);
  };

  const handlePracticeQuestions = () => {
    // Use the latestChapter derived from learning_current_level
    const params = new URLSearchParams({
      subject: selectedSubject,
      chapter: latestChapter,
    });

    navigate(`/practice/sampleWorksheet?${params.toString()}`);
  };

  const handleVerifyOfflineWork = () => {
    navigate("/practice/evaluateAnswerSheet?mode=new");
  };

  const handleTakeMasteryTest = () => {
    // Use the latestChapter derived from learning_current_level
    activateMasteryFlow();
    const params = new URLSearchParams({
      subject: selectedSubject,
      chapter: latestChapter,
      from: "masteryTest",
    });

    navigate(`/practice/sampleWorksheet?${params.toString()}`);
  };

  const handleSolveWorkbook = () => {
    if (!selectedSubject || !currentTopic) {
      navigate("/tutor");
      return;
    }

    const params = new URLSearchParams({
      enableWorkbookmode: "true",
      tab: "myUploads",
    });

    // if (selectedChapterDetails?.docId) {
    //   params.set("docId", selectedChapterDetails.docId);
    // }

    const isPracticeFlow = location.pathname.startsWith("/practice");
    const targetBasePath = isPracticeFlow ? "/practice/tutor" : "/tutor";
    navigate(`${targetBasePath}?${params.toString()}`);
  };

  const handleNotesPractice = () => {
    navigate("/practice/notes");
  };

  const handleChangeTopic = () => {
    const normalizedSubject =
      typeof selectedSubject === "string" ? selectedSubject.trim() : "";
    const fallbackSubject = subjects.find(
      (subject) => typeof subject === "string" && subject.trim().length > 0
    );
    const subjectForNavigation = normalizedSubject || fallbackSubject || "";

    const chapterForNavigation =
      typeof currentTopic === "string" && currentTopic.trim().length > 0
        ? currentTopic.trim()
        : "";

    const params = new URLSearchParams();
    if (subjectForNavigation) {
      params.set("subject", subjectForNavigation);
    }
    if (chapterForNavigation) {
      params.set("chapter", chapterForNavigation);
    }
    params.set("flow", "change-topic");

    const statePayload = {
      from: "change-topic",
    };

    if (subjectForNavigation) {
      statePayload.subject = subjectForNavigation;
    }

    if (chapterForNavigation) {
      statePayload.chapter = chapterForNavigation;
    }

    navigate(`/new-learn-2?${params.toString()}`, {
      state: statePayload,
    });
  };

  const handleLevelSelect = (levelId) => {
    setSelectedLevel(levelId);
  };

  const handleSetLevelAndStart = async () => {
    if (!selectedLevel || !selectedChapter) {
      return;
    }

    const levelDetails = proficiencyLevels.find(
      (level) => level.id === selectedLevel
    );
    const selfAssessmentScore = levelDetails?.value;

    if (selfAssessmentScore === undefined) {
      console.error("Invalid proficiency level selected");
      return;
    }

    const chapterTitle =
      typeof selectedChapter === "string"
        ? selectedChapter
        : selectedChapter?.title || selectedChapter?.name || "";

    if (!chapterTitle) {
      return;
    }

    const normalizedSubject =
      typeof selectedSubject === "string"
        ? selectedSubject.trim()
        : selectedSubject?.title || selectedSubject?.name || "";

    const fallbackSubject = (
      subjects.find(
        (subject) => typeof subject === "string" && subject.trim().length > 0
      ) || ""
    ).trim();

    const subjectForApi = normalizedSubject || fallbackSubject;
    if (!subjectForApi) {
      console.error("No subject available for self assessment");
      return;
    }

    const subjectKey = normalizedSubject || selectedSubject || subjectForApi;
    const userId = userData?.userId;
    if (!userId) {
      console.error("No user ID available for self assessment update");
      return;
    }

    setIsSettingLevel(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        setIsSettingLevel(false);
        return;
      }

      const selfAssessmentResponse = await axios.post(
        `${baseUrl}/v2/quiz/update-self-assessment-lo`,
        {
          user_id: userId,
          subject: subjectForApi,
          chapter: chapterTitle,
          self_assessment_score: selfAssessmentScore,
        },
        {
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const responsePayload =
        (selfAssessmentResponse?.data?.result &&
        typeof selfAssessmentResponse.data.result === "object"
          ? selfAssessmentResponse.data.result
          : null) ??
        (selfAssessmentResponse?.data &&
        typeof selfAssessmentResponse.data === "object"
          ? selfAssessmentResponse.data
          : null);

      const apiShieldPercentage = parseShieldPercentage(
        responsePayload?.shield_percentage ?? responsePayload?.shieldPercentage
      );
      const resolvedShieldPercentage =
        apiShieldPercentage !== null
          ? clampPercent(apiShieldPercentage)
          : clampPercent(selfAssessmentScore);
      const apiLoValue = parseFiniteNumber(
        responsePayload?.lo ??
          responsePayload?.current_lo ??
          responsePayload?.currentLo
      );
      const apiLevelValue = parseFiniteNumber(
        responsePayload?.level ??
          responsePayload?.level_id ??
          responsePayload?.levelId
      );
      const normalizedApiLevel =
        apiLevelValue !== null
          ? Math.min(Math.max(Math.round(apiLevelValue), 1), 4)
          : null;

      // Get current learning_current_level or initialize it
      const currentLearningLevel = userData?.learning_current_level || {};
      const gradeKey = userGrade ? `${userGrade}th Grade` : "10th Grade";

      // Preserve existing grade data or initialize
      const gradeData = currentLearningLevel[gradeKey] || {};

      // Map level IDs to numeric values
      const levelMap = {
        beginner: 1,
        intermediate: 2,
        proficient: 3,
        advanced: 4,
      };

      const levelValue = levelMap[selectedLevel] || 1;
      const resolvedLevelValue =
        normalizedApiLevel !== null ? normalizedApiLevel : levelValue;
      const resolvedLoValue = apiLoValue !== null ? apiLoValue : 2; // default LO value

      // Update the selected subject's data while preserving other subjects
      const updatedLearningLevel = {
        ...currentLearningLevel,
        [gradeKey]: {
          ...gradeData,
          [subjectKey]: {
            chapter: chapterTitle,
            lo: resolvedLoValue,
            level: resolvedLevelValue,
            shield_percentage: resolvedShieldPercentage,
          },
        },
      };

      // Call update-profile API
      const response = await axios.put(
        `${baseUrl}/v2/profile/update-profile`,
        {
          learning_current_level: updatedLearningLevel,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Update UserContext with the new learning_current_level
      if (response.data?.learning_current_level) {
        updateProfileDetails({
          learning_current_level: response.data.learning_current_level,
        });
      } else {
        // Fallback: fetch the updated profile
        await fetchUserProfile();
      }

      setIsSettingLevel(false);

      setIsModalOpen(false);
      // Update all UI state to reflect the new chapter immediately
      // Set currentTopic first to ensure UI updates immediately
      setCurrentTopic(chapterTitle);
      setShouldBypassLearningLevel(true);
      setSelectedChapter(null);
      setSelectedLevel(null);

      return;
    } catch (error) {
      console.error("Error updating learning level:", error);
      setIsSettingLevel(false);
      // Still close modal and set chapter even if API call fails
      setIsModalOpen(false);
      setCurrentTopic(chapterTitle);
      setShouldBypassLearningLevel(true);
      setSelectedChapter(null);
      setSelectedLevel(null);

      // Force a small delay to ensure UserContext update propagates, then verify the topic is set correctly
      setTimeout(() => {
        if (currentTopic !== chapterTitle) {
          setCurrentTopic(chapterTitle);
        }
      }, 100);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLevel(null);
    setSelectedChapter(null);
  };

  const handleChapterClick = (chapter) => {
    setSelectedChapter(chapter);
    setIsModalOpen(true);
  };

  // Get chapters for selected subject
  const chaptersForSelectedSubject =
    selectedSubject &&
    subjectChapters &&
    Array.isArray(subjectChapters[selectedSubject]) &&
    subjectChapters[selectedSubject].length > 0
      ? subjectChapters[selectedSubject]
      : [];

  const chapters = chaptersForSelectedSubject.map((chapterName, index) => {
    if (typeof chapterName === "string") {
      return { id: index + 1, title: chapterName };
    }
    if (chapterName && typeof chapterName === "object") {
      return {
        id: chapterName.id || index + 1,
        title: chapterName.title || chapterName.name || `Chapter ${index + 1}`,
      };
    }
    return { id: index + 1, title: `Chapter ${index + 1}` };
  });

  // Filter chapters based on search query
  const filteredChapters = chapters.filter((chapter) =>
    chapter.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const availableSubjects =
      Array.isArray(syllabusSubjects) && syllabusSubjects.length > 0
        ? syllabusSubjects
        : DEFAULT_SUBJECTS;

    if (!availableSubjects.length) return;

    // If selected subject has data in learning_current_level, set the chapter
    // This ensures UI updates when learning_current_level changes
    // But only update if the chapter from learning_current_level is different from currentTopic
    if (hasSubjectData && currentLevelChapter) {
      if (
        doesSubjectContainChapter(selectedSubject, currentLevelChapter) &&
        currentTopic !== currentLevelChapter
      ) {
        // Only update if the chapter is actually different to avoid overriding manual updates
        setCurrentTopic(currentLevelChapter);
      }
    }

    // Decide which subject should be active:
    // - On first load with a valid preferredSubject, use it
    // - Afterwards, keep the user's selectedSubject as long as it's valid
    // - Otherwise, fall back to the first available subject
    let nextSubject = null;

    const preferredIsValid =
      preferredSubject && availableSubjects.includes(preferredSubject);
    const selectedIsValid =
      selectedSubject && availableSubjects.includes(selectedSubject);
    const hasAppliedPreferred = hasAppliedPreferredRef.current;

    if (preferredIsValid && !hasAppliedPreferred) {
      nextSubject = preferredSubject;
    } else if (selectedIsValid) {
      nextSubject = selectedSubject;
    } else {
      nextSubject = availableSubjects[0];
    }

    if (nextSubject !== selectedSubject) {
      setSelectedSubject(nextSubject);
    }

    // Decide topic for the chosen subject:
    // - On first load, if preferredChapter is valid for this subject, use it
    // - Otherwise, if currentTopic is invalid for this subject, use the first chapter
    let nextTopic = currentTopic;
    const isApplyingPreferredChapter =
      preferredChapter &&
      !hasAppliedPreferred &&
      preferredIsValid &&
      nextSubject === preferredSubject &&
      doesSubjectContainChapter(nextSubject, preferredChapter);

    if (isApplyingPreferredChapter) {
      nextTopic = preferredChapter;
    } else if (!doesSubjectContainChapter(nextSubject, currentTopic)) {
      const firstChapter = getFirstChapterForSubject(nextSubject);
      if (firstChapter) {
        nextTopic = firstChapter;
      }
    }

    if (nextTopic !== currentTopic) {
      setCurrentTopic(nextTopic);
    }

    if (isApplyingPreferredChapter) {
      setShouldBypassLearningLevel(true);
    }

    // Mark that we've consumed the preferred values so user tab switches aren't overridden
    if (preferredIsValid && !hasAppliedPreferred) {
      hasAppliedPreferredRef.current = true;
    }
  }, [
    syllabusSubjects,
    preferredSubject,
    preferredChapter,
    selectedSubject,
    currentTopic,
    doesSubjectContainChapter,
    getFirstChapterForSubject,
    hasSubjectData,
    currentLevelChapter,
    learningCurrentLevel, // Add dependency to trigger when learning_current_level changes
  ]);

  if (!syllabusContext || syllabusLoading) {
    return (
      <div className="h-[100dvh] flex overflow-hidden bg-white">
        <div className="hidden md:block">
          <Sidebar />
        </div>
        <div className="flex flex-1 flex-col">
          <div className="flex-1 flex items-center justify-center">
            <SimpleThreeDotsLoader message="Loading your syllabus..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex overflow-hidden bg-white">
      {/* Sidebar - shown only on md+ screens */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Mobile Drawer */}
      <MobileSidebarDrawer
        open={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
      <div className="fixed top-0 left-0 right-0 z-20 h-1/10 pointer-events-none">
        <Header />
      </div>
      {/* Main Content */}
      <div className="flex flex-col h-full w-full">
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile Topbar */}
          <MobileTopbar
            onMenuClick={() => setIsMobileMenuOpen(true)}
            showMenuButton={true}
            showProfileButton={false}
            showEditButton={false}
          />

          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto px-4 md:px-8 pb-10">
            <div className="w-full pt-[4%] mx-auto">
              {/* Subject Navigation Tabs */}
              <div className="w-full flex justify-center mb-3">
                <div className="flex gap-2 md:gap-3 overflow-x-auto  -mx-2 px-2 md:mx-0 md:px-0 justify-center items-center bg-gray-100 rounded-full">
                  {subjects.map((subject) => (
                    <button
                      key={subject}
                      onClick={() => {
                        setSelectedSubject(subject);
                        persistLearningSubjectSelection(subject);
                        const {
                          entry: subjectLevelData,
                          hasEntry: subjectHasEntry,
                        } = getSubjectEntryFromLearningLevel(subject);
                        if (subjectHasEntry && subjectLevelData) {
                          const levelChapterName =
                            getChapterNameFromLevelEntry(subjectLevelData);
                          if (
                            levelChapterName &&
                            doesSubjectContainChapter(subject, levelChapterName)
                          ) {
                            setCurrentTopic(levelChapterName);
                            return;
                          }
                        }
                        const firstChapter = getFirstChapterForSubject(subject);
                        if (firstChapter) {
                          setCurrentTopic(firstChapter);
                        }
                      }}
                      className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                        selectedSubject === subject
                          ? "bg-[#0D99FF] text-white shadow-md"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {subject}
                    </button>
                  ))}
                </div>
              </div>

              {/* Show loader while fetching, then chapter content if available, otherwise show manual selection */}
              {showSubjectDataLoader ? (
                <div className="py-16 flex justify-center">
                  <SimpleThreeDotsLoader message="Loading your learning plan..." />
                </div>
              ) : shouldShowLearningView ? (
                <>
                  {/* Topic Header */}
                  <div className="flex items-center gap-4 md:gap-6 mb-3">
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
                      {topicData.title}
                    </h1>
                    <button
                      onClick={handleChangeTopic}
                      className="text-sm md:text-base text-[#0D99FF] hover:text-[#6351E0] underline font-medium whitespace-nowrap"
                    >
                      Change Topic
                    </button>
                  </div>

                  {/* Progress Bar Section */}
                  <div className="mb-6 md:mb-8">
                    <ShieldProgressBar
                      progress={topicData.progress}
                      mastery={topicData.mastery}
                      nextGoal={topicData.nextGoal}
                    />
                    {/* <p className="text-xs md:text-sm text-gray-500 mt-3">
                      {topicData.nextShield
                        ? `${topicData.remainingToNextShield}% more to reach ${topicData.nextShield.label}`
                        : "You've unlocked the Diamond Shield—keep it up!"}
                    </p> */}
                  </div>

                  {/* Your Daily Learning Loop */}
                  <div className="mb-8 md:mb-10">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mt-6 md:mt-8 mb-4 md:mb-6">
                      Your Daily Learning Loop
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                      {/* Learn Concepts Card */}
                      <button
                        onClick={handleLearnConcepts}
                        className="bg-white border-2 border-gray-200 rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 text-left"
                      >
                        <div className="flex flex-col items-center text-center">
                          {/* <div className="mb-4">
                            <svg
                              width="64"
                              height="64"
                              viewBox="0 0 64 64"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-16 h-16"
                            >
                              <path
                                d="M18 22L32 16L46 22L46 30L32 36L18 30L18 22Z"
                                fill="#0D99FF"
                              />
                              ]<path
                                d="M18 30L32 36L32 46L18 40L18 30Z"
                                fill="#6351E0"
                              />
                              ]<path
                                d="M32 36L46 30L46 40L32 46L32 36Z"
                                fill="#5A47D6"
                              />
                            </svg>
                          </div> */}
                          <div className="mb-4">
                            <img
                              src="/teachme.jpeg"
                              alt="Adaptive practice illustration"
                              className="w-20 h-20 object-contain"
                              loading="lazy"
                            />
                          </div>
                          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                            Start a New Lesson
                          </h3>
                          <p className="text-sm text-gray-500">
                            Master concepts and learn new topics
                          </p>
                        </div>
                      </button>

                      {/* Practice Questions Card - Active/Highlighted */}
                      <button
                        onClick={handlePracticeQuestions}
                        className="bg-white border-3 border-[#FF6B00] rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 text-left"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4">
                            <img
                              src="/adaptive2.jpeg"
                              alt="Adaptive practice illustration"
                              className="w-20 h-20 object-contain"
                              loading="lazy"
                            />
                          </div>
                          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                            Adaptive Practice
                          </h3>
                          <p className="text-sm text-gray-500">
                            Question difficulty adapted to your current level
                            (Gain Power)
                          </p>
                        </div>
                      </button>

                      {/* Verify Offline Work Card */}
                      <button
                        onClick={handleVerifyOfflineWork}
                        className="bg-white border-2 border-gray-200 rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 text-left"
                      >
                        <div className="flex flex-col items-center text-center">
                          {/* <div className="mb-4">
                            <svg
                              width="64"
                              height="64"
                              viewBox="0 0 64 64"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-16 h-16"
                            >
                              <rect
                                x="16"
                                y="12"
                                width="32"
                                height="40"
                                rx="2"
                                fill="#E0E7FF"
                                stroke="#0D99FF"
                                strokeWidth="2"
                              />
                              <rect
                                x="20"
                                y="18"
                                width="24"
                                height="2"
                                fill="#0D99FF"
                              />
                              <rect
                                x="20"
                                y="24"
                                width="20"
                                height="2"
                                fill="#0D99FF"
                              />
                              <rect
                                x="20"
                                y="30"
                                width="24"
                                height="2"
                                fill="#0D99FF"
                              />
                              <circle cx="40" cy="40" r="6" fill="#0D99FF" />
                              <path
                                d="M37 40L39 42L43 38"
                                stroke="#FFFFFF"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          </div> */}
                          <div className="mb-4">
                            <img
                              src="/offline.jpeg"
                              alt="Adaptive practice illustration"
                              className="w-20 h-20 object-contain"
                              loading="lazy"
                            />
                          </div>
                          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                            Check My Offline Work
                          </h3>
                          <p className="text-sm text-gray-500">
                            Upload your answer sheet for instant & precise
                            feedback (Gain Power)
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Bottom Section - Take Mastery Test and Practice Tools */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    {/* Take Mastery Test Card */}
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-200 flex flex-col justify-center align-center">
                      <button
                        onClick={handleTakeMasteryTest}
                        className="w-full text-left"
                      >
                        <div className="flex flex-row items-center gap-4">
                          {/* <FaGem
                            className="w-12 h-12"
                            style={{ color: "#F97316" }}
                          /> */}

                          <div className="flex-shrink-0">
                            <img
                              src="/mastery5.jpeg"
                              alt="Adaptive practice illustration"
                              className="w-48 h-48 object-contain"
                              loading="lazy"
                            />
                          </div>

                          <div className="flex-1">
                            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                              Take Mastery Test
                            </h3>
                            <p className="text-sm md:text-base text-gray-600">
                              Earn your Gold and Diamond. Show your Power !
                            </p>
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Practice Tools Section */}
                    <div className="space-y-2 md:space-y-3">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900">
                        Practice Tools
                      </h3>

                      {/* Solve Workbook Card */}
                      <button
                        onClick={handleSolveWorkbook}
                        className="w-full bg-white border-2 border-gray-200 rounded-2xl p-4 md:p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 text-left"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0">
                            <img
                              src="/doubt.jpeg"
                              alt="Adaptive practice illustration"
                              className="w-20 h-20 object-contain"
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-1">
                              Solve My Doubts
                            </h4>
                            <p className="text-xs md:text-sm text-gray-600">
                              Get instant homework help, nobody can stop you now
                            </p>
                          </div>
                        </div>
                      </button>

                      {/* Notes Practice Card */}
                      {/* <button
                        onClick={handleNotesPractice}
                        className="w-full bg-white border-2 border-gray-200 rounded-2xl p-4 md:p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 text-left"
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0">
                            <img
                              src="/notes.jpeg"
                              alt="Adaptive practice illustration"
                              className="w-20 h-20 object-contain"
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-1">
                              Review My Notes
                            </h4>
                            <p className="text-xs md:text-sm text-gray-600">
                              Turn study time into quiz time!
                            </p>
                          </div>
                        </div>
                      </button> */}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Chapter List View - when subject has no data in learning_current_level */}
                  {/* Search Bar */}
                  <div className="mb-6 md:mb-8">
                    <div className="relative">
                      <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="What chapter are you learning this week?"
                        className="w-full pl-12 pr-4 py-3 md:py-4 rounded-2xl border-2 border-gray-200 focus:border-[#0D99FF] focus:outline-none text-sm md:text-base"
                      />
                    </div>
                  </div>

                  {/* Chapters Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {filteredChapters.map((chapter) => (
                      <button
                        key={chapter.id}
                        onClick={() => handleChapterClick(chapter)}
                        className="bg-white border-2 border-gray-200 rounded-xl p-4 md:p-6 hover:border-[#0D99FF] hover:shadow-lg transition-all duration-200 hover:-translate-y-1 text-left"
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-lg md:text-xl font-bold text-gray-400">
                            {chapter.id}.
                          </span>
                          <span className="text-base md:text-lg font-semibold text-gray-900 flex-1">
                            {chapter.title}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* No results message */}
                  {filteredChapters.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-gray-500 text-lg">
                        No chapters found matching "{searchQuery}"
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Proficiency Level Selection Modal */}
      {isModalOpen && selectedChapter && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl p-6 md:p-8 relative">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition p-2"
              aria-label="Close modal"
            >
              <FiX className="w-6 h-6" />
            </button>

            {/* Modal Content */}
            <div className="mt-4">
              {/* Question */}
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 md:mb-12">
                How do you feel about{" "}
                {selectedChapter?.title ||
                  selectedChapter?.name ||
                  selectedChapter}
                ?
              </h2>

              {/* Proficiency Level Cards */}
              <div className="flex flex-col sm:flex-row gap-4 md:gap-6 mb-8 md:mb-12 items-end">
                {proficiencyLevels.map((level) => {
                  const isSelected = selectedLevel === level.id;
                  return (
                    <button
                      key={level.id}
                      onClick={() => handleLevelSelect(level.id)}
                      className={`flex-1 ${
                        level.height
                      } rounded-2xl p-4 md:p-6 transition-all duration-200 ${
                        isSelected
                          ? "bg-[#0D99FF] text-white shadow-lg scale-105"
                          : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                      }`}
                    >
                      <div className="flex flex-col h-full justify-between">
                        <div>
                          <h3
                            className={`text-sm md:text-base font-bold uppercase mb-2 ${
                              isSelected ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {level.title}
                          </h3>
                          <p
                            className={`text-sm md:text-base font-semibold mb-2 ${
                              isSelected ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {level.subtitle}
                          </p>
                          <p
                            className={`text-xs md:text-sm ${
                              isSelected ? "text-white/90" : "text-gray-600"
                            }`}
                          >
                            {level.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Set Level & Start Learning Button */}
              <div className="flex justify-center mt-6 md:mt-8 pt-6 md:pt-8">
                {isSettingLevel ? (
                  <div className="flex flex-col items-center gap-3">
                    <SimpleThreeDotsLoader message="Updating your learning plan..." />
                  </div>
                ) : (
                  <button
                    onClick={handleSetLevelAndStart}
                    disabled={!selectedLevel}
                    className={`px-8 md:px-12 py-3 md:py-4 rounded-xl md:rounded-2xl text-base md:text-lg font-semibold transition-all duration-200 ${
                      selectedLevel
                        ? "bg-[#0D99FF] text-white hover:bg-[#6351E0] hover:shadow-lg hover:-translate-y-0.5"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Set Level & Start Learning
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewLearnComponent;
